import { useMemo, useState, useRef, TouchEvent } from "react";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { BasketsTabBasket } from "./BasketsTabBasket";
import { BasketsTabTotalsTable } from "./BasketsTabTotalsTable";
import { useSearchMixDetailsContext } from "../../context/SearchMixDetailsContext";
import { BasketMaterial } from "components/basketPlot/types";
import { useIsMobile } from "hooks/useIsMobile";

export const BasketsTabContent = () => {
  const { mixBasketsSummary, mixMaterialBasketsSummary, sortedMaterials } =
    useSearchMixDetailsContext();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [currentBasketIndex, setCurrentBasketIndex] = useState(0);
  const touchStartXRef = useRef<number | null>(null);

  const basketsData = useMemo(() => {
    const sortedBasketsSummary = [...mixBasketsSummary].sort(
      (a, b) => a.basket_number - b.basket_number
    );

    return sortedBasketsSummary.map((basketSummary) => {
      const basketMaterials = mixMaterialBasketsSummary
        .filter(
          (material) => material.basket_number === basketSummary.basket_number
        )
        .sort((a, b) => a.basket_layer_number - b.basket_layer_number);

      const totalMass = basketSummary.recipe_mass;

      const materials: BasketMaterial[] = basketMaterials
        .filter((material) => material.recipe_mass > 0)
        .map((material) => ({
          name: material.material,
          mass: material.recipe_mass,
          color:
            sortedMaterials.find((m) => m.name === material.material)?.color ??
            theme.palette.text.primary,
          basketLayer: material.basket_layer,
        }));

      return {
        basketNumber: basketSummary.basket_number,
        totalMass,
        fullness: basketSummary.fullness,
        materials,
      };
    });
  }, [
    mixBasketsSummary,
    mixMaterialBasketsSummary,
    sortedMaterials,
    theme.palette.text.primary,
  ]);

  const handlePrevious = () => {
    setCurrentBasketIndex((prev) =>
      prev > 0 ? prev - 1 : basketsData.length - 1
    );
  };

  const handleNext = () => {
    setCurrentBasketIndex((prev) =>
      prev < basketsData.length - 1 ? prev + 1 : 0
    );
  };

  const handleTouchStart = (e: TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const touchEndX = e.changedTouches[0]?.clientX ?? 0;
    const diff = touchStartXRef.current - touchEndX;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
    }
    touchStartXRef.current = null;
  };

  if (isMobile) {
    const currentBasket = basketsData[currentBasketIndex];
    if (!currentBasket) return null;

    return (
      <Box
        sx={{ width: "100%", my: 2 }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ mb: 2 }}
          gap={2}
        >
          <IconButton
            onClick={handlePrevious}
            disabled={basketsData.length <= 1}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {currentBasketIndex + 1} / {basketsData.length}
          </Typography>
          <IconButton
            onClick={handleNext}
            disabled={basketsData.length <= 1}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <BasketsTabBasket
            basketNumber={currentBasket.basketNumber}
            totalMass={currentBasket.totalMass}
            fullness={currentBasket.fullness}
            materials={currentBasket.materials}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
        <Box sx={{ flexShrink: 0 }}>
          <BasketsTabTotalsTable />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: 2,
            justifyContent: "space-around",
          }}
        >
          {basketsData.map((basket) => (
            <BasketsTabBasket
              key={basket.basketNumber}
              basketNumber={basket.basketNumber}
              totalMass={basket.totalMass}
              fullness={basket.fullness}
              materials={basket.materials}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
